/**
 * Janome JEF. 머리글(오프셋·날짜·색 수·점 수·틀 크기·범위) + 색 번호 목록 + 바늘땀(부호 있는 1바이트 dx, -dy).
 * 명령: 0x80 0x01 색 바꿈, 0x80 0x02 이동(점프), 0x80 0x10 끝. 트림은 제자리 이동 3번으로 적는다.
 * 구조는 pyembroidery (MIT) 의 JefWriter.py 를 옮겼다. NOTICE.md 참고.
 */
import { COLOR, END, JUMP, STITCH, type StitchPlan, TRIM } from "../engine/plan";
import { ByteWriter } from "./bytes";
import { hexToInt, JEF_PALETTE, nearestIndex } from "./palettes";

const HOOP_110X110 = 0,
  HOOP_50X50 = 1,
  HOOP_140X200 = 2,
  HOOP_126X110 = 3,
  HOOP_200X200 = 4;

function hoopSize(w: number, h: number): number {
  if (w < 500 && h < 500) return HOOP_50X50;
  if (w < 1260 && h < 1100) return HOOP_126X110;
  if (w < 1400 && h < 2000) return HOOP_140X200;
  if (w < 2000 && h < 2000) return HOOP_200X200;
  return HOOP_110X110;
}

function edge(w: ByteWriter, x: number, y: number) {
  if (Math.min(x, y) >= 0) {
    w.i32(x);
    w.i32(y);
    w.i32(x);
    w.i32(y);
  } else {
    w.i32(-1);
    w.i32(-1);
    w.i32(-1);
    w.i32(-1);
  }
}

export function jefDate(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

export function writeJef(plan: StitchPlan, date = jefDate()): Uint8Array {
  const TRIM_REPEAT = 3;
  // 색 번호 (연속 같은 번호를 피한다)
  const palette: number[] = [];
  let last = -1;
  for (const c of plan.colors) {
    let idx = nearestIndex(hexToInt(c), JEF_PALETTE);
    if (idx === last) {
      const alt = JEF_PALETTE.slice();
      alt[idx] = null;
      idx = nearestIndex(hexToInt(c), alt);
    }
    palette.push(idx);
    last = idx;
  }
  const colorCount = palette.length;
  let pointCount = 1;
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (let i = 0; i < plan.cmds.length; i++) {
    const c = plan.cmds[i];
    if (c === STITCH) pointCount += 1;
    else if (c === JUMP) pointCount += 2;
    else if (c === TRIM) pointCount += 2 * TRIM_REPEAT;
    else if (c === COLOR) pointCount += 2;
    else if (c === END) break;
    if (c === STITCH || c === JUMP) {
      x0 = Math.min(x0, plan.xs[i]);
      y0 = Math.min(y0, plan.ys[i]);
      x1 = Math.max(x1, plan.xs[i]);
      y1 = Math.max(y1, plan.ys[i]);
    }
  }
  if (!isFinite(x0)) x0 = y0 = x1 = y1 = 0;
  const w = new ByteWriter(4096 + plan.cmds.length * 4);
  w.i32(0x74 + colorCount * 8);
  w.i32(0x14);
  w.ascii(date.slice(0, 14).padEnd(14, "0"));
  w.u8(0);
  w.u8(0);
  w.i32(colorCount);
  w.i32(pointCount);
  const dw = Math.round(x1 - x0),
    dh = Math.round(y1 - y0);
  w.i32(hoopSize(dw, dh));
  const hw = Math.round(dw / 2),
    hh = Math.round(dh / 2);
  w.i32(hw);
  w.i32(hh);
  w.i32(hw);
  w.i32(hh);
  edge(w, 550 - hw, 550 - hh);
  edge(w, 250 - hw, 250 - hh);
  edge(w, 700 - hw, 1000 - hh);
  edge(w, 700 - hw, 1000 - hh);
  for (const p of palette) w.i32(p);
  for (let i = 0; i < colorCount; i++) w.i32(0x0d);
  let px = 0,
    py = 0;
  for (let i = 0; i < plan.cmds.length; i++) {
    const c = plan.cmds[i];
    const x = plan.xs[i],
      y = plan.ys[i];
    const dx = x - px,
      dy = y - py;
    if (c === STITCH) {
      w.i8(dx);
      w.i8(-dy);
    } else if (c === COLOR) {
      w.bytes([0x80, 0x01]);
      w.i8(dx);
      w.i8(-dy);
    } else if (c === TRIM) {
      for (let k = 0; k < TRIM_REPEAT; k++) w.bytes([0x80, 0x02, 0x00, 0x00]);
    } else if (c === JUMP) {
      w.bytes([0x80, 0x02]);
      w.i8(dx);
      w.i8(-dy);
    } else if (c === END) break;
    px = x;
    py = y;
  }
  w.bytes([0x80, 0x10]);
  return w.result();
}
